// Active-event store — mirrors apps/ios/Shared/Services/ActiveEventStore.swift
// for the browser. URL is the source of truth for the *current* event
// (route params `orgSlug` + `eventSlug`); the store holds the joined-events
// list and resolves slug pairs → numeric ids.
//
// activate() runs on every event-route mount: it calls BA's
// /organization/set-active so the session row's activeOrganizationId
// stays in lockstep with the URL. Tenant scoping is by org now (BA teams
// retired in favor of QB-native event_roles).

import { useEffect } from "react"
import { useParams } from "@tanstack/react-router"
import { create } from "zustand"
import { api } from "./api"
import { invalidateJwt } from "./auth"
import { backendUrl } from "./backend-url"
import { authApiPath, isV2Api } from "./api-contract"
import { useUserOrgs } from "./orgs"
import type { MyEventRow } from "./types"
import { v2Api } from "./v2/client"
import type {
  V2OrganizationEvent,
  V2OrganizationEventsPage,
} from "./v2/contracts"

type State = {
  events: MyEventRow[]
  loading: boolean
  loaded: boolean
  error: string | null
  /// Set after `activate()` succeeds — the eventId currently mirrored into
  /// the BA session. Used to skip redundant set-active calls when the user
  /// navigates between sub-routes of the same event.
  activeEventId: string | null
  /// The orgId currently pinned in the BA session. Tracked separately from
  /// activeEventId so org-scoped routes (/<org>/settings/...) can dedup
  /// their set-active calls without going through the event-activation path.
  activeOrgId: string | null
  load: () => Promise<void>
  bySlug: (orgSlug: string, eventSlug: string) => MyEventRow | undefined
  byId: (id: string) => MyEventRow | undefined
  /// Lookup helper for store consumers OUTSIDE React render. React
  /// callers should select the orgId primitive directly to keep
  /// reference equality stable (see useActiveOrg).
  orgIdBySlug: (orgSlug: string) => string | undefined
  /// Sync BA's session with the given event. No-op if already active.
  activate: (eventId: string, options?: { force?: boolean }) => Promise<void>
  /// Pin the BA session to an org without resolving an event — used by the
  /// /<org>/ portal and /<org>/settings/* routes. No-op if already active.
  activateOrg: (orgId: string, options?: { force?: boolean }) => Promise<void>
  /// Clear BA's active org so the session is org-less. Called by the root
  /// `/` (org picker) — that page must have NO active org; only a URL with
  /// an `:orgSlug` (via activate/activateOrg) sets one. No-op if already clear.
  clearOrg: () => Promise<void>
}

// Better Auth endpoints exposed by the `organization` plugin. Hit via the
// fetch wrapper directly (not `api()`) because they live under `/auth/v1/*`
// instead of `/api/v1/*` and rely on the BA session cookie.
async function baFetch(path: string, body: unknown) {
  const res = await fetch(backendUrl(authApiPath(path)), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => "")
    throw new Error(`${path} failed: ${res.status} ${text}`)
  }
  return res.json().catch(() => ({}))
}

// Coalesce concurrent activate(eventId) calls. Without this, every
// useActiveEvent() effect + every api() call's ensureActiveOrgForCurrentRoute
// races the same set-active before the first one's `set({activeEventId})`
// lands, and we get a /auth/v1/organization/set-active storm.
const activateInFlight = new Map<string, Promise<void>>()
// Same coalescing for org-only activation paths.
const activateOrgInFlight = new Map<string, Promise<void>>()

async function ensureUserOrgsLoaded(): Promise<void> {
  let state = useUserOrgs.getState()
  if (state.loaded) return
  if (!state.loading) void state.load()
  await new Promise<void>((resolve) => {
    if (useUserOrgs.getState().loaded) return resolve()
    const unsub = useUserOrgs.subscribe((next) => {
      if (next.loaded) {
        unsub()
        resolve()
      }
    })
  })
  state = useUserOrgs.getState()
  if (!state.loaded) throw new Error("Failed to load organizations")
}

/// Awaits the admin-events list (useEvents) being loaded — kicks the load if
/// it hasn't started. Non-hook; safe to call from a router loader. The events
/// list is what bySlug() resolves against, so callers that need a slug→eventId
/// resolution must await this first.
async function ensureEventsLoaded(): Promise<void> {
  let state = useEvents.getState()
  if (state.loaded) return
  if (!state.loading) void state.load()
  await new Promise<void>((resolve) => {
    if (useEvents.getState().loaded) return resolve()
    const unsub = useEvents.subscribe((s) => {
      if (s.loaded) {
        unsub()
        resolve()
      }
    })
  })
}

function orgIdBySlugFromStores(orgSlug: string): string | undefined {
  return (
    useUserOrgs.getState().orgs.find((org) => org.slug === orgSlug)?.id ??
    useEvents.getState().orgIdBySlug(orgSlug)
  )
}

export const useEvents = create<State>((set, get) => ({
  events: [],
  loading: false,
  loaded: false,
  error: null,
  activeEventId: null,
  activeOrgId: null,
  async load() {
    set({ loading: true, error: null })
    try {
      const events = isV2Api
        ? await loadV2OrganizationEvents()
        : (
            await api<{ events: MyEventRow[] }>("/api/v1/me/admin-events")
          ).events
      set({
        events: events ?? [],
        loading: false,
        loaded: true,
        error: null,
      })
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load events"
      set({ loading: false, loaded: true, error: msg })
    }
  },
  bySlug(orgSlug, eventSlug) {
    return get().events.find(
      (e) => e.organizationSlug === orgSlug && e.slug === eventSlug,
    )
  },
  byId(id) {
    return get().events.find((e) => e.eventId === id)
  },
  orgIdBySlug(orgSlug) {
    return get().events.find((e) => e.organizationSlug === orgSlug)?.organizationId ?? undefined
  },
  async activate(eventId, options) {
    // Skip when the BA session is already pinned to this event's org —
    // BA `set-active` is idempotent but the extra round-trip is
    // wasteful on sub-route navigation.
    if (!options?.force && get().activeEventId === eventId) return
    const existing = activateInFlight.get(eventId)
    if (existing) return existing
    const p = (async () => {
      const row = get().byId(eventId)
      if (!row?.organizationId) {
        throw new Error(`No event ${eventId} in joined-events list`)
      }
      await baFetch("/organization/set-active", {
        organizationId: row.organizationId,
      })
      // Bust the JWT cache so the next mint picks up the freshly-set
      // activeOrganizationId. Without this, queued /api/v1/* calls in
      // this tab present a stale JWT and 403 on org-firewalled actions.
      invalidateJwt()
      set({
        activeEventId: eventId,
        activeOrgId: row.organizationId,
      })
    })()
    activateInFlight.set(eventId, p)
    try {
      await p
    } finally {
      activateInFlight.delete(eventId)
    }
  },
  async activateOrg(orgId, options) {
    // Dedup: if the BA session is already pinned to this org, skip the
    // network call entirely. Org-scoped settings pages call this on
    // mount; without the guard, every sub-route navigation re-fires
    // set-active for an org that's already active.
    if (!options?.force && get().activeOrgId === orgId) return
    const existing = activateOrgInFlight.get(orgId)
    if (existing) return existing
    const p = (async () => {
      await baFetch("/organization/set-active", { organizationId: orgId })
      // Bust JWT so the next mint picks up the new activeOrganizationId.
      invalidateJwt()
      set({
        activeOrgId: orgId,
        // Org changed → previous event activation is no longer valid;
        // clear so a later activate(eventId) re-runs instead of short-
        // circuiting on the stale event id.
        activeEventId: null,
      })
    })()
    activateOrgInFlight.set(orgId, p)
    try {
      await p
    } finally {
      activateOrgInFlight.delete(orgId)
    }
  },
  async clearOrg() {
    // Already org-less → nothing to do (and avoids re-firing on every
    // root re-render). Clear local state first so a concurrent re-fire
    // short-circuits, then unset BA's session active org + bust the JWT
    // so the next mint carries no orgId.
    if (get().activeOrgId === null) return
    set({ activeOrgId: null, activeEventId: null })
    invalidateJwt()
    try {
      await baFetch("/organization/set-active", { organizationId: null })
    } catch {
      // Best-effort — local state is already cleared; the server session
      // reconciles on the next set-active or sign-in.
    }
  },
}))

async function loadV2OrganizationEvents(): Promise<MyEventRow[]> {
  await ensureUserOrgsLoaded()
  const organizations = useUserOrgs.getState().orgs
  const rows: MyEventRow[] = []

  // The v2 control-plane catalog is intentionally organization-scoped. The
  // active-org session claim is part of each JWT, so catalog orgs must be read
  // serially; parallelizing would race the claim and risk cross-org results.
  for (const organization of organizations) {
    await useEvents.getState().activateOrg(organization.id)
    let cursor: V2OrganizationEventsPage["page"]["nextCursor"] = null
    do {
      const page: V2OrganizationEventsPage = await v2Api<V2OrganizationEventsPage>(
        `/api/v2/organizations/${encodeURIComponent(organization.id)}/events`,
        {
          searchParams: {
            limit: 100,
            cursorStartsAt: cursor?.startsAt,
            cursorEventId: cursor?.eventId,
          },
          ensureRouteScope: false,
        },
      )
      rows.push(
        ...page.events.map((event: V2OrganizationEvent) =>
          organizationEventToViewState(event, organization.slug),
        ),
      )
      cursor = page.page.hasMore ? page.page.nextCursor : null
    } while (cursor)
  }

  await useEvents.getState().clearOrg()
  return rows
}

export function organizationEventToViewState(
  event: V2OrganizationEvent,
  organizationSlug: string,
): MyEventRow {
  return {
    eventId: event.id,
    organizationId: event.organizationId,
    organizationSlug,
    name: event.name,
    slug: event.slug,
    description: event.description,
    startsAt: event.startsAt,
    endsAt: event.endsAt,
    ianaTimezone: event.ianaTimezone,
    venueName: event.venueName,
    addressLocality: event.addressLocality,
    addressRegion: event.addressRegion,
    status: event.status,
    // V2 stores the R2 media identity, not a trusted public URL. The console
    // must wait for the media read contract instead of guessing a files URL.
    coverImageUrl: null,
    primaryColor: event.primaryColor,
    hostName: null,
    directMessagesEnabled: null,
    enableSchedule: null,
    enableTravel: null,
    enableAccommodation: null,
    enableChannels: null,
    enableConcierge: null,
  }
}

export function resetEventsStore() {
  useEvents.setState({
    events: [],
    loading: false,
    loaded: false,
    error: null,
    activeEventId: null,
    activeOrgId: null,
  })
}

/** Current resolved route scope for non-hook transport adapters. */
export function currentOrganizerScope(): {
  eventId: string | undefined
  organizationId: string | undefined
} {
  const state = useEvents.getState()
  const active = state.events.find((row) => row.eventId === state.activeEventId)
  return {
    eventId: state.activeEventId ?? undefined,
    organizationId: active?.organizationId ?? state.activeOrgId ?? undefined,
  }
}

/// Awaited by api.ts before every /api/v1/* mint so a page that mounts
/// queries on the FIRST render doesn't fire them with a no-org JWT
/// while useActiveEvent's effect is still in flight. Idempotent — does
/// nothing when the URL isn't event/org-scoped, when the events list hasn't
/// loaded yet (no slug→id resolution possible), or when the target is
/// already activated.
///
/// Handles three URL shapes (all set BA's active ORG — teams are retired):
///   /<org>/<event>/...          → activate(eventId) [resolves org from event]
///   /<org>/settings/...         → activateOrg(orgId)
///   /<org>/                     → activateOrg(orgId) [event picker etc.]
///
/// "settings" + "clients" are reserved at segment[1] (i.e. /<org>/settings/*,
/// /<org>/clients/*) so an event can't take those slugs. This mirrors the
/// worker's reserved-slug list.
export async function ensureActiveOrgForCurrentRoute(
  options: { force?: boolean } = {},
): Promise<void> {
  if (typeof window === "undefined") return
  const segments = window.location.pathname.split("/").filter(Boolean)
  if (segments.length < 1) return
  // Reserved prefixes that aren't an org slug.
  const reserved = new Set(["account", "api", "auth", "broadcast", "realtime", "invite", "health", "openapi.json", "settings", "admin", "sign-in", "set-password"])
  const orgSlug = segments[0]
  if (reserved.has(orgSlug)) return
  // Detect URL shape from segment[1].
  const second = segments[1]
  // Event-reserved sub-paths under /<org>/ that AREN'T an event slug.
  const orgScopedReserved = new Set(["settings", "clients"])

  if (!second || orgScopedReserved.has(second)) {
    // Org-only scope. Resolve from the BA org-membership list first so
    // orgs with zero events still activate correctly on settings pages.
    await ensureUserOrgsLoaded()
    const state = useEvents.getState()
    const orgId = orgIdBySlugFromStores(orgSlug)
    if (!orgId) return
    if (!options.force && state.activeOrgId === orgId) return
    return state.activateOrg(orgId, options)
  }

  // Event scope: /<org>/<event>/...
  // Block until events list has loaded — event routes resolve
  // orgSlug/eventSlug → ids from the administrable event list.
  await ensureEventsLoaded()
  const state = useEvents.getState()

  const eventSlug = second
  const row = state.bySlug(orgSlug, eventSlug)
  if (!row?.eventId) return
  if (!options.force && state.activeEventId === row.eventId) return
  return state.activate(row.eventId, options)
}

/**
 * Pin the account session to the organization that owns an exact event.
 * Canonical API requests use this instead of window.location so router intent
 * preloads cannot accidentally inherit the page that happens to be visible.
 */
export async function ensureActiveOrgForEvent(
  eventId: string,
  options: { force?: boolean } = {},
): Promise<void> {
  await ensureEventsLoaded()
  const state = useEvents.getState()
  const row = state.byId(eventId)
  if (!row?.organizationId) {
    throw new Error(`No event ${eventId} in joined-events list`)
  }
  await state.activate(eventId, options)
}

/** Pin the account session to one explicit organization-scoped API target. */
export async function ensureActiveOrganization(
  organizationId: string,
  options: { force?: boolean } = {},
): Promise<void> {
  await useEvents.getState().activateOrg(organizationId, options)
}

/// Resolve a URL slug pair → the real eventId, awaiting the admin-events list
/// if it hasn't loaded yet. Non-hook; safe to call from a router loader (where
/// hooks/stores-as-hooks aren't available). Returns undefined when the slugs
/// don't match any administrable event (e.g. bad URL, or the user lost access).
export async function resolveEventId(
  orgSlug: string,
  eventSlug: string,
): Promise<string | undefined> {
  await ensureEventsLoaded()
  return useEvents.getState().bySlug(orgSlug, eventSlug)?.eventId
}

/// Resolve a URL orgSlug → orgId. Awaits both the BA org-membership list and
/// the admin-events list (org may have zero events, or live only in the events
/// list). Non-hook; safe to call from a router loader.
export async function resolveOrgId(orgSlug: string): Promise<string | undefined> {
  await Promise.all([ensureUserOrgsLoaded().catch(() => {}), ensureEventsLoaded()])
  return orgIdBySlugFromStores(orgSlug)
}

/// Convenience hook used by every page rendered under `$orgSlug/$eventSlug`.
/// Reads the URL slugs, resolves to a MyEventRow, fires the BA set-active
/// sync when the event changes, and returns the resolved ids so the page
/// can render with the numeric eventId it actually wants.
///
/// Returns undefined fields when the slugs don't match (events list still
/// loading, or invalid URL). Pages should gate their content on `eventId`
/// being defined.
export function useActiveEvent(): {
  orgSlug: string | undefined
  eventSlug: string | undefined
  eventId: string | undefined
  organizationId: string | undefined
  row: MyEventRow | undefined
} {
  const params = useParams({ strict: false }) as {
    orgSlug?: string
    eventSlug?: string
  }
  const { orgSlug, eventSlug } = params
  const row = useEvents((s) =>
    orgSlug && eventSlug ? s.bySlug(orgSlug, eventSlug) : undefined,
  )
  const activate = useEvents((s) => s.activate)

  useEffect(() => {
    if (!row?.eventId) return
    void activate(row.eventId).catch((err) => {
      console.warn("[active-event] set-active failed", err)
    })
  }, [row?.eventId, activate])

  return {
    orgSlug,
    eventSlug,
    eventId: row?.eventId,
    organizationId: row?.organizationId ?? undefined,
    row,
  }
}

/// Hook for /<org>/settings/* routes. Resolves the URL orgSlug → orgId from
/// the BA org-membership list (falling back to event rows) and fires
/// activateOrg() when the BA session is pinned to a different org.
/// Org-only — there's no team concept (BA teams retired), so this never
/// touches event activation.
export function useActiveOrg(): {
  orgSlug: string | undefined
  organizationId: string | undefined
} {
  const params = useParams({ strict: false }) as { orgSlug?: string }
  const { orgSlug } = params
  const orgsLoaded = useUserOrgs((s) => s.loaded)
  const orgsLoading = useUserOrgs((s) => s.loading)
  const loadOrgs = useUserOrgs((s) => s.load)
  const orgIdFromOrgs = useUserOrgs((s) =>
    orgSlug ? s.orgs.find((org) => org.slug === orgSlug)?.id : undefined,
  )
  const orgIdFromEvents = useEvents((s) =>
    orgSlug ? s.orgIdBySlug(orgSlug) : undefined,
  )
  const organizationId = orgIdFromOrgs ?? orgIdFromEvents
  const activateOrg = useEvents((s) => s.activateOrg)

  useEffect(() => {
    if (!orgsLoaded && !orgsLoading) void loadOrgs()
  }, [orgsLoaded, orgsLoading, loadOrgs])

  useEffect(() => {
    if (!organizationId) return
    void activateOrg(organizationId).catch((err) => {
      console.warn("[active-org] set-active failed", err)
    })
  }, [organizationId, activateOrg])

  return {
    orgSlug,
    organizationId,
  }
}
